import React from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { Colors } from '@/constants/colors';

interface MarkdownTextProps {
  text: string;
}

/**
 * Lightweight markdown renderer
 * Supports: #, ##, ### headers, unordered lists, bold (**text**), italics (*text*), code fences (```).
 * Designed to avoid extra deps and keep a consistent look across app.
 */
export const MarkdownText: React.FC<MarkdownTextProps> = ({ text }) => {
  if (!text) return null;

  // Handle code fences first
  const blocks = splitCodeFences(text);

  return (
    <View>
      {blocks.map((block, idx) => {
        if (block.type === 'code') {
          return (
            <View key={idx} style={styles.codeBlock}>
              <Text style={styles.codeText}>{block.content}</Text>
            </View>
          );
        }

        // Non-code markdown paragraphs
        const paragraphs = block.content.split(/\n{2,}/);
        return paragraphs.map((paragraph, i) => {
          // Tables (GitHub-style)
          if (isTableBlock(paragraph)) {
            const { headers, aligns, rows } = parseTable(paragraph);
            return (
              <View key={`${idx}-${i}`} style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeaderRow]}>
                  {headers.map((h, ci) => (
                    <View key={ci} style={[styles.tableCell, styles.tableHeaderCell]}>
                      <Text
                        style={[
                          styles.tableHeaderText,
                          aligns[ci] === 'center' && { textAlign: 'center' },
                          aligns[ci] === 'right' && { textAlign: 'right' },
                        ]}
                      >
                        {h}
                      </Text>
                    </View>
                  ))}
                </View>
                {rows.map((r, ri) => (
                  <View key={ri} style={styles.tableRow}>
                    {r.map((c, ci) => (
                      <View key={ci} style={styles.tableCell}>
                        <Text
                          style={[
                            styles.tableCellText,
                            aligns[ci] === 'center' && { textAlign: 'center' },
                            aligns[ci] === 'right' && { textAlign: 'right' },
                          ]}
                        >
                          {c}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            );
          }
          // Headers
          if (paragraph.startsWith('# ')) {
            return (
              <Text key={`${idx}-${i}`} style={styles.h1}>
                {paragraph.replace(/^#\s/, '')}
              </Text>
            );
          }
          if (paragraph.startsWith('## ')) {
            return (
              <Text key={`${idx}-${i}`} style={styles.h2}>
                {paragraph.replace(/^##\s/, '')}
              </Text>
            );
          }
          if (paragraph.startsWith('### ')) {
            return (
              <Text key={`${idx}-${i}`} style={styles.h3}>
                {paragraph.replace(/^###\s/, '')}
              </Text>
            );
          }

          // Lists
          const lines = paragraph.split('\n');
          if (lines.every(line => /^(\-|\*)\s+/.test(line.trim()))) {
            return (
              <View key={`${idx}-${i}`} style={styles.list}>
                {lines.map((line, li) => (
                  <Text key={li} style={styles.listItem}>
                    {line.replace(/^(\-|\*)\s+/, '• ')}
                  </Text>
                ))}
              </View>
            );
          }

          // Inline bold/italic within paragraph
          const formatted = formatInline(paragraph);

          return (
            <Text key={`${idx}-${i}`} style={styles.paragraph}>
              {formatted}
            </Text>
          );
        });
      })}
    </View>
  );
};

function splitCodeFences(src: string): Array<{ type: 'code' | 'text'; content: string }> {
  const result: Array<{ type: 'code' | 'text'; content: string }> = [];
  const parts = src.split(/```/);
  for (let i = 0; i < parts.length; i++) {
    const content = parts[i];
    if (i % 2 === 1) {
      // code block
      result.push({ type: 'code', content: content.trim() });
    } else if (content) {
      result.push({ type: 'text', content });
    }
  }
  return result;
}

function formatInline(paragraph: string) {
  // Replace bold **text** and italics *text* with appropriate styles
  const tokens: Array<{ text: string; style?: 'bold' | 'italic' }> = [];
  let i = 0;
  while (i < paragraph.length) {
    if (paragraph.slice(i, i + 2) === '**') {
      const end = paragraph.indexOf('**', i + 2);
      if (end !== -1) {
        tokens.push({ text: paragraph.slice(i + 2, end), style: 'bold' });
        i = end + 2;
        continue;
      }
    }
    if (paragraph[i] === '*' && paragraph[i + 1] !== ' ') {
      const end = paragraph.indexOf('*', i + 1);
      if (end !== -1) {
        tokens.push({ text: paragraph.slice(i + 1, end), style: 'italic' });
        i = end + 1;
        continue;
      }
    }
    // Regular text until next marker
    const nextMarkers = [
      paragraph.indexOf('**', i),
      paragraph.indexOf('*', i),
    ].filter(n => n !== -1);
    const next = nextMarkers.length ? Math.min(...nextMarkers) : -1;
    if (next === -1) {
      tokens.push({ text: paragraph.slice(i) });
      break;
    } else {
      tokens.push({ text: paragraph.slice(i, next) });
      i = next;
    }
  }

  return tokens.map((t, idx) => {
    if (t.style === 'bold') {
      return (
        <Text key={idx} style={styles.bold}>
          {t.text}
        </Text>
      );
    }
    if (t.style === 'italic') {
      return (
        <Text key={idx} style={styles.italic}>
          {t.text}
        </Text>
      );
    }
    return <Text key={idx}>{t.text}</Text>;
  });
}

function isTableBlock(text: string) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return false;
  const hasPipes = lines[0].includes('|') && lines[1].includes('|');
  const headerSep = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;
  return hasPipes && headerSep.test(lines[1]);
}

function parseTable(text: string): {
  headers: string[];
  aligns: Array<'left' | 'center' | 'right'>;
  rows: string[][];
} {
  const lines = text.trim().split('\n').filter(Boolean);
  const header = lines[0];
  const sep = lines[1];
  const body = lines.slice(2);

  const splitRow = (row: string) =>
    row
      .replace(/^\s*\|/, '')
      .replace(/\|\s*$/, '')
      .split('|')
      .map(c => c.trim());

  const headers = splitRow(header);
  const seps = splitRow(sep);

  const aligns = seps.map(s => {
    const left = s.startsWith(':');
    const right = s.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });

  const rows = body.map(splitRow);
  return { headers, aligns, rows };
}

const styles = StyleSheet.create({
  h1: {
    fontSize: 24,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 12,
  },
  h2: {
    fontSize: 20,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 16,
    marginBottom: 10,
  },
  h3: {
    fontSize: 17,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 12,
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 15,
    color: Colors.text.secondary,
    lineHeight: 22,
    marginBottom: 12,
  },
  list: {
    marginBottom: 12,
  },
  listItem: {
    fontSize: 15,
    color: Colors.text.secondary,
    lineHeight: 22,
    marginBottom: 4,
  },
  bold: {
    fontWeight: '700',
    color: Colors.text.secondary,
  },
  italic: {
    fontStyle: 'italic',
    color: Colors.text.secondary,
  },
  codeBlock: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    marginBottom: 12,
  },
  codeText: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: Colors.text.secondary,
    lineHeight: 20,
  },
  table: {
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    borderRadius: 8,
    marginBottom: 12,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  tableHeaderRow: {
    backgroundColor: Colors.background.secondary,
  },
  tableCell: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRightWidth: 1,
    borderRightColor: Colors.border.subtle,
  },
  tableHeaderCell: {
    borderRightWidth: 1,
    borderRightColor: Colors.border.subtle,
  },
  tableHeaderText: {
    fontSize: 13,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  tableCellText: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
});


